import "../components/page-outline.js";
import { renderContentComparison } from "../content-diff.js";
import { contentHistoryComparisonOptions, decodeContentHistory, contentHistorySelection, populateContentVersionControls, readContentResponse } from "../content-history.js";

function readHistoricalDocument(html, version, canonicalPath) {
  const template = document.createElement("template");
  template.innerHTML = html;
  const main = template.content.querySelector("main[data-content-version]");
  const body = main?.querySelector("#document-content");
  const heading = main?.querySelector(":scope > header");
  const title = template.content.querySelector("title");
  if (main?.dataset.contentVersion !== version || main.dataset.contentCanonicalPath !== canonicalPath || !body || !heading || !title
    || body.querySelector("script,iframe,object,embed,form")
    || heading.querySelector("script,iframe,object,embed,form")) throw new TypeError("Historical content is invalid");
  const metadata = {
    title: heading.querySelector("h1")?.textContent ?? "",
    description: template.content.querySelector('meta[name="description"]')?.getAttribute("content") ?? "",
    authors: Array.from(template.content.querySelectorAll('meta[name="author"]'), (node) => node.getAttribute("content")).join("\n"),
    dates: Array.from(heading.querySelectorAll("time"), (node) => node.parentElement.textContent.trim()).join("\n"),
  };
  return { body, heading, title: title.textContent, metadata };
}

function renderMetadataChanges(before, after, locale) {
  const fields = [
    ["title", "Title", "Titel"], ["description", "Description", "Beschreibung"],
    ["authors", "Authors", "Autoren"], ["dates", "Publication and update dates", "Veröffentlichungs- und Änderungsdaten"],
  ].filter(([key]) => before[key] !== after[key]);
  if (!fields.length) return null;
  const german = locale === "de-DE";
  const details = document.createElement("details");
  details.className = "collapse collapse-arrow not-prose mb-5 border border-base-300 bg-base-100";
  details.setAttribute("data-content-metadata-comparison", "");
  details.innerHTML = `<summary class="collapse-title font-semibold">${german ? "Geänderte Metadaten" : "Metadata changes"}</summary><div class="collapse-content text-sm"><dl class="grid gap-4"></dl></div>`;
  const list = details.querySelector("dl");
  for (const [key, english, translated] of fields) {
    const row = document.createElement("div");
    row.className = "grid min-w-0 gap-2";
    row.innerHTML = `<dt class="font-semibold">${german ? translated : english}</dt><dd class="m-0 grid min-w-0 gap-3 sm:grid-cols-2"><div class="min-w-0"><strong>${german ? "Links" : "Left"}</strong><p class="m-0 whitespace-pre-wrap break-words" data-before></p></div><div class="min-w-0"><strong>${german ? "Rechts" : "Right"}</strong><p class="m-0 whitespace-pre-wrap break-words" data-after></p></div></dd>`;
    row.querySelector("[data-before]").textContent = before[key] || (german ? "Nicht angegeben" : "Not specified");
    row.querySelector("[data-after]").textContent = after[key] || (german ? "Nicht angegeben" : "Not specified");
    list.append(row);
  }
  return details;
}

function mountContentHistory(main, refreshContent = false) {
  const lifetime = new AbortController();
  const root = main.querySelector("[data-content-history]");
  const content = main.querySelector("#document-content");
  const outline = document.querySelector('fishy-page-outline[for="document-content"]');
  const status = root.querySelector("[data-content-history-status]");
  const locale = document.documentElement.lang;
  const canonicalPath = main.dataset.contentCanonicalPath;
  const requestedUrl = new URL(window.location.href);
  const fetchText = async (href, type, cache = "default") => readContentResponse(await fetch(href, {
    cache, signal: lifetime.signal, credentials: "same-origin", redirect: "error", headers: { Accept: type },
  }), type);
  const requestedView = requestedUrl.searchParams;
  const replacing = refreshContent || requestedUrl.pathname !== canonicalPath || requestedView.has("version") || requestedView.has("compare");
  // Never show another revision's prose under a requested historical/comparison URL.
  if (replacing) {
    content.classList.add("invisible");
    if (outline) outline.contentElement = null;
  }
  content.setAttribute("aria-busy", "true");
  status.textContent = locale === "de-DE" ? "Fassungen werden geladen…" : "Loading versions…";
  void (async () => {
    try {
      const manifest = decodeContentHistory(JSON.parse(await fetchText(main.dataset.contentHistoryPath, "application/json", "no-cache")), { locale, canonicalPath });
      const pathVersion = requestedUrl.pathname.startsWith(`${canonicalPath}versions/`)
        ? requestedUrl.pathname.slice(`${canonicalPath}versions/`.length).replace(/\/$/, "") : null;
      const selection = contentHistorySelection(manifest, requestedUrl.search, pathVersion || manifest.defaultVersion);
      const { baseline, view, displayed } = selection;
      if (displayed && (refreshContent || baseline || displayed.version !== main.dataset.contentVersion)) {
        const [afterHtml, beforeHtml] = await Promise.all([
          fetchText(displayed.href, "text/html", manifest.kind === "editorial" ? "no-cache" : "default"),
          view === "diff" ? fetchText(baseline.href, "text/html", manifest.kind === "editorial" ? "no-cache" : "default") : null,
        ]);
        if (lifetime.signal.aborted) return;
        const after = readHistoricalDocument(afterHtml, displayed.version, canonicalPath);
        if (view === "diff") {
          const before = readHistoricalDocument(beforeHtml, baseline.version, canonicalPath);
          content.replaceChildren(renderContentComparison(document, before.body, after.body, locale, contentHistoryComparisonOptions(manifest)));
          if (manifest.kind === "editorial") {
            const metadata = renderMetadataChanges(before.metadata, after.metadata, locale);
            if (metadata) content.prepend(metadata);
          }
        } else {
          content.replaceChildren(...after.body.childNodes);
        }
        main.querySelector(":scope > header").replaceWith(after.heading);
        document.title = after.title;
      }
      if (lifetime.signal.aborted) return;
      populateContentVersionControls(root, manifest, selection);
      content.classList.remove("invisible");
      content.removeAttribute("aria-busy");
      // Publishing a revision replaces heading nodes as well as their anchors.
      if (outline) outline.contentElement = content;
      status.textContent = "";
      main.dataset.contentVersion = displayed?.version ?? "";
    } catch (error) {
      if (lifetime.signal.aborted) return;
      content.classList.toggle("invisible", replacing);
      content.removeAttribute("aria-busy");
      status.textContent = locale === "de-DE"
        ? "Die angeforderte Fassung oder der Vergleich ist nicht verfügbar. Verwenden Sie die Direktlinks oder die Standardansicht."
        : "The requested version or comparison is unavailable. Use the direct links or default view.";
      globalThis.reportError?.(error);
    }
  })();
  return () => lifetime.abort();
}

const main = document.querySelector("main[data-content-history-path]");
if (main?.dataset.contentHistoryPath && main.querySelector("[data-content-history]")) {
  let dispose = mountContentHistory(main);
  let route = location.pathname + location.search;
  const canonical = main.dataset.contentCanonicalPath;
  // Canonical pages may own independent account/acceptance lifecycles. Cross
  // their document boundary natively rather than deleting or recreating them.
  const nativeCanonical = (url) => main.dataset.contentNativeCanonical === "true"
    && url.pathname === canonical && !url.searchParams.has("compare");
  const navigate = () => {
    if (nativeCanonical(new URL(location.href))) {
      location.reload();
      return;
    }
    route = location.pathname + location.search;
    dispose();
    dispose = mountContentHistory(main, true);
  };
  main.addEventListener("click", (event) => {
    const link = event.target.closest?.("[data-content-history] a[href]");
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
      || link.hasAttribute("download") || (link.target && link.target !== "_self")) return;
    const url = new URL(link.href);
    const revisionPath = url.pathname.startsWith(`${canonical}versions/`)
      && /^(?:sha256-[A-Za-z0-9_-]{43}|[a-z0-9][a-z0-9-]{0,63})\/$/.test(url.pathname.slice(`${canonical}versions/`.length));
    // Default canonical reading always uses its complete static document.
    if (url.origin !== location.origin || nativeCanonical(new URL(location.href))
      || (url.pathname === canonical && !url.searchParams.has("compare"))
      || (!revisionPath && !(url.pathname === canonical && url.searchParams.has("version")))) return;
    event.preventDefault();
    main.querySelector("dialog[open]")?.close();
    history.pushState(null, "", url);
    navigate();
  });
  window.addEventListener("popstate", () => {
    if (route !== location.pathname + location.search) navigate();
  });
  window.addEventListener("pagehide", () => dispose());
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) dispose = mountContentHistory(main, true);
  });
}
